<script>
import { mapGetters, mapActions } from 'vuex';
import MensagemList from './MensagemList.vue';
import MensagemComposer from './MensagemComposer.vue';
import PainelPerfil from './painel/PainelPerfil.vue';
import PainelEtiquetas from './painel/PainelEtiquetas.vue';
import PainelNotas from './painel/PainelNotas.vue';

const PAINEL_TABS = [
  { key: 'perfil',     label: 'Perfil',     icone: 'ri-user-line' },
  { key: 'etiquetas',  label: 'Etiquetas',  icone: 'ri-price-tag-3-line' },
  { key: 'notas',      label: 'Notas',      icone: 'ri-sticky-note-line' },
];

export default {
  name: 'ChatDrawer',
  components: { MensagemList, MensagemComposer, PainelPerfil, PainelEtiquetas, PainelNotas },
  // inline=false (default): overlay fixed à direita (modo kanban).
  // inline=true: renderiza dentro do layout flex do parent (modo lista
  // tipo WhatsApp Web), com empty state quando não há conversa ativa.
  props: {
    inline: { type: Boolean, default: false },
  },
  data() {
    return {
      painelAberto: this._lerPainelAberto(),
      painelTab: 'perfil',
      tabs: PAINEL_TABS,
      // Janela 24h da Meta — calculada por contato. null = ainda
      // carregando, true/false = resposta do backend.
      janelaInfo: null,
      janelaCarregando: false,
      // Templates (cache simples — só carrega uma vez por sessão).
      templates: [],
      // Modal de confirmação de envio de template.
      templateModal: null,
      enviandoTemplate: false,
      // Estado do seletor de template dentro do modal.
      templateEscolhidoName: '',
      templateValoresEscolhidos: {},
      // Modal de finalizar conversa.
      finalizarModalAberto: false,
      mensagemFinalTexto: '',
      finalizandoConversa: false,
      // Dropdown de estágio (Kanban) no header do drawer.
      estagioDropAberto: false,
      // Posição calculada do dropdown — usado com Teleport para escapar
      // do overflow:hidden do container do chat e ficar visível.
      estagioDropPos: { top: '0px', left: '0px' },
      // Menu de ações no mobile: 3-pontos que agrupa Assumir/Liberar/Opt-out/
      // Finalizar/painel. Teleportado (escapa do overflow), posição fixa.
      menuAcoesAberto: false,
      menuAcoesPos: { top: '0px', right: '0px' },
    };
  },
  computed: {
    ...mapGetters('atendimento', ['conversaAtiva', 'mensagensDaAtiva', 'estagios']),
    ...mapGetters('auth', ['currentUser']),
    aberto() { return Boolean(this.conversaAtiva); },
    // No modo inline o componente sempre aparece (mostra empty state quando
    // não há conversa). No modo overlay só aparece quando há conversa ativa.
    visivel() { return this.inline || this.aberto; },
    nomeContato() {
      const c = this.conversaAtiva;
      if (!c) return '';
      const ct = c.contato || {};
      const nome = String(ct.nome || '').trim();
      const sobrenome = String(ct.sobrenome || '').trim();
      if (nome && sobrenome) return `${nome} ${sobrenome}`;
      if (nome) return nome;
      if (ct.id) return `Contato #${ct.id}`;
      return 'Conversa';
    },
    // Título da barra: nome cadastrado → username Meta → telefone → #id.
    // Não exibe o número junto do nome — o número só aparece como fallback.
    tituloBarra() {
      const c = this.conversaAtiva;
      if (!c) return '';
      const ct = c.contato || {};
      const nome = String(ct.nome || '').trim();
      const sobrenome = String(ct.sobrenome || '').trim();
      if (nome) return sobrenome ? `${nome} ${sobrenome}` : nome;
      const canais = Array.isArray(ct.canais) ? ct.canais : [];
      const comUser = canais.find((x) => x.whatsapp_username);
      if (comUser) return String(comUser.whatsapp_username).trim();
      if (this.telefoneFormatado) return this.telefoneFormatado;
      if (ct.id) return `Contato #${ct.id}`;
      return 'Conversa';
    },
    // Subtítulo do painel: username do WhatsApp (preferência) ou o telefone.
    subtituloBarra() {
      const ct = (this.conversaAtiva && this.conversaAtiva.contato) || {};
      const canais = Array.isArray(ct.canais) ? ct.canais : [];
      const comUser = canais.find((x) => x.whatsapp_username);
      if (comUser) return String(comUser.whatsapp_username).trim();
      return this.telefoneFormatado || '';
    },
    iniciaisAvatar() {
      const nome = this.nomeContato || '';
      const partes = nome.trim().split(/\s+/).slice(0, 2);
      return partes.map((p) => p.charAt(0).toUpperCase()).join('') || '?';
    },
    telefoneFormatado() {
      const c = this.conversaAtiva;
      if (!c || !c.contato || !c.contato.canais) return '';
      const tel = c.contato.canais.find((x) => x.tipo_canal === 'telefone' && x.principal)
        || c.contato.canais.find((x) => x.tipo_canal === 'telefone');
      if (!tel) return '';
      const v = tel.valor_canal || tel.normalizado || '';
      // formata 5531998000000 → +55 (31) 99800-0000
      const m = String(v).replace(/\D/g, '').match(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/);
      return m ? `+${m[1]} (${m[2]}) ${m[3]}-${m[4]}` : v;
    },
    estagio() { return this.conversaAtiva && this.conversaAtiva.estagio ? this.conversaAtiva.estagio : null; },
    atendente() { return this.conversaAtiva && this.conversaAtiva.atendente ? this.conversaAtiva.atendente : null; },
    podeAssumir() { return this.conversaAtiva && !this.conversaAtiva.atendente_id; },
    eMinha() { return this.conversaAtiva && this.currentUser && this.conversaAtiva.atendente_id === this.currentUser.id; },
    statusConsentimento() { return (this.conversaAtiva && this.conversaAtiva.status_consentimento) || 'pendente'; },
    bloqueadoPorOptOut() { return this.statusConsentimento === 'opt_out'; },
    contatoAtivo() {
      return this.conversaAtiva ? (this.conversaAtiva.contato || null) : null;
    },
    // null = desconhecido (carregando ou sem mensagem entrante prévia).
    // true/false vem da consulta /conversa/janela/:contatoId.
    janelaAberta() {
      return this.janelaInfo ? Boolean(this.janelaInfo.aberta) : null;
    },
    bloqueadoPorJanela() {
      // Bloqueia texto livre quando a Meta exige template (janela fechada
      // OU nunca houve mensagem entrante). Ainda permite envio de template
      // pelo CTA do banner.
      return this.janelaAberta === false;
    },
    composerDisabled() {
      return !this.conversaAtiva || this.bloqueadoPorOptOut || this.bloqueadoPorJanela;
    },
    templatePadrao() {
      return (this.templates || []).find((t) => t.padrao_inicial && t.status === 'APPROVED') || null;
    },
    templatesAprovados() {
      // Compat com formato legado (`aprovado: true`) E novo (`status: 'APPROVED'`).
      return (this.templates || []).filter((t) => t.status === 'APPROVED' || t.aprovado);
    },
    templateEscolhido() {
      return this.templatesAprovados.find((t) => t.name === this.templateEscolhidoName) || null;
    },
    previewTemplateEscolhido() {
      return this._renderizarPreview(this.templateEscolhido, this.templateValoresEscolhidos);
    },
    templateValoresValidos() {
      const tpl = this.templateEscolhido;
      if (!tpl) return false;
      const obrigatorios = (tpl.parametros || []).filter((p) => p.obrigatorio);
      return obrigatorios.every((p) => Boolean(String(this.templateValoresEscolhidos[p.nome] || '').trim()));
    },
    nomePrimeiroContato() {
      const ct = this.contatoAtivo || {};
      return String(ct.nome || '').trim().split(/\s+/)[0] || '';
    },
    expiraJanelaFmt() {
      const d = this.janelaInfo && this.janelaInfo.ultima_entrada;
      if (!d) return '';
      try { return new Date(d).toLocaleString('pt-BR'); } catch (_e) { return ''; }
    },
  },
  watch: {
    aberto(v) {
      // ESC só faz sentido no modo overlay (drawer fecha com Esc).
      if (this.inline) return;
      if (v) this._addEscListener();
      else this._removeEscListener();
    },
    // Recarrega o estado da janela 24h sempre que muda de conversa.
    'conversaAtiva.id': {
      immediate: true,
      handler(novoId) {
        if (!novoId) {
          this.janelaInfo = null;
          this.templateModal = null;
          return;
        }
        this._carregarJanela();
        if (!this.templates.length) this._carregarTemplates();
      },
    },
    // Quando o atendente troca de template no select, refaz o prefill
    // dos parâmetros (mantém valores já digitados quando possível).
    templateEscolhidoName() {
      this._prefillTemplateValores();
    },
    // Detecta nova mensagem entrante na conversa ativa e refaz a consulta
    // da janela 24h em tempo real — assim o composer é liberado assim que
    // o filiado responde a um template, sem precisar trocar de conversa.
    mensagensDaAtiva: {
      handler(novas, antigas) {
        if (!novas || !antigas || !antigas.length) return;
        if (novas.length <= antigas.length) return;
        const idsAntigas = new Set(antigas.map((m) => m.id));
        const houveEntrante = novas.some(
          (m) => m && m.direcao === 'entrada' && !idsAntigas.has(m.id),
        );
        if (houveEntrante) this._carregarJanela();
      },
    },
    // Conversa desselecionada: reseta o painel e limpa a classe/flag do body.
    conversaAtiva(nova) {
      if (!nova) {
        this._painelHistoryPushed = false;
        document.body.classList.remove('atendimento-painel');
        if (this._ehMobile()) this.painelAberto = false;
      }
    },
  },
  mounted() {
    this._painelHistoryPushed = false;
    document.addEventListener('click', this._fecharEstagiosFora);
    document.addEventListener('click', this._fecharMenuAcoesFora);
    window.addEventListener('popstate', this._onPainelPop);
  },
  beforeUnmount() {
    this._removeEscListener();
    document.removeEventListener('click', this._fecharEstagiosFora);
    document.removeEventListener('click', this._fecharMenuAcoesFora);
    window.removeEventListener('popstate', this._onPainelPop);
    document.body.classList.remove('atendimento-painel');
  },
  methods: {
    ...mapActions('atendimento', ['selecionarConversa', 'enviarMensagem', 'assumirConversa', 'liberarConversa', 'registrarOptOut', 'verificarJanela', 'fetchTemplates', 'iniciarConversa', 'finalizarConversa', 'moverConversa']),

    fechar() { this.selecionarConversa(null); },

    // ── Dropdown de estágio (mover conversa pelo header) ────────────
    toggleEstagios(e) {
      if (e) e.stopPropagation();
      if (!this.estagioDropAberto) {
        // Calcula posição em viewport (fixed) a partir do retângulo do botão.
        const btn = this.$refs.estagioBtn;
        if (btn && typeof btn.getBoundingClientRect === 'function') {
          const r = btn.getBoundingClientRect();
          this.estagioDropPos = {
            top: `${r.bottom + 4}px`,
            left: `${r.left}px`,
          };
        }
      }
      this.estagioDropAberto = !this.estagioDropAberto;
    },
    async selecionarEstagio(estagioId) {
      this.estagioDropAberto = false;
      if (!this.conversaAtiva || this.conversaAtiva.estagio_id === estagioId) return;
      try {
        await this.moverConversa({ conversaId: this.conversaAtiva.id, estagioId });
      } catch (error) {
        const msg = error?.response?.data?.error || 'Falha ao mover conversa.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
      }
    },
    _fecharEstagiosFora(e) {
      if (this.estagioDropAberto && !e.target.closest('.cd-stage-selector')) {
        this.estagioDropAberto = false;
      }
    },

    // ── Menu de ações no mobile (3-pontos) ─────────────────────────
    toggleMenuAcoes(e) {
      if (e) e.stopPropagation();
      if (!this.menuAcoesAberto) {
        const btn = this.$refs.acoesBtn;
        if (btn && typeof btn.getBoundingClientRect === 'function') {
          const r = btn.getBoundingClientRect();
          this.menuAcoesPos = {
            top: `${r.bottom + 6}px`,
            right: `${Math.max(8, window.innerWidth - r.right)}px`,
          };
        }
      }
      this.menuAcoesAberto = !this.menuAcoesAberto;
    },
    _fecharMenuAcoesFora(e) {
      if (this.menuAcoesAberto
        && !e.target.closest('.cd-acoes-menu')
        && !e.target.closest('.cd-acoes-dropdown')) {
        this.menuAcoesAberto = false;
      }
    },
    acaoDoMenu(fn) {
      this.menuAcoesAberto = false;
      if (typeof fn === 'function') fn();
    },

    async _carregarJanela() {
      if (!this.contatoAtivo || !this.contatoAtivo.id) return;
      this.janelaCarregando = true;
      try {
        this.janelaInfo = await this.verificarJanela(this.contatoAtivo.id);
      } catch (_e) {
        this.janelaInfo = { aberta: false, motivo: 'erro-consulta' };
      } finally {
        this.janelaCarregando = false;
      }
    },
    async _carregarTemplates() {
      try {
        // Filtra pelo canal da conversa ativa — evita listar templates de
        // outros canais que não enviam mais.
        const canalId = this.conversaAtiva && this.conversaAtiva.canal_whatsapp_id;
        const resp = await this.fetchTemplates(canalId || null);
        // Backend novo retorna { canal, templates }; legado retorna array.
        this.templates = Array.isArray(resp) ? resp : (resp && resp.templates) || [];
      } catch (_e) { /* noop */ }
    },
    _renderizarPreview(tpl, valores) {
      if (!tpl || !tpl.preview) return '';
      let txt = tpl.preview;
      for (const p of (tpl.parametros || [])) {
        const v = String((valores || {})[p.nome] ?? '');
        txt = txt.replace(new RegExp(`\\{\\{\\s*${p.indice}\\s*\\}\\}`, 'g'), v);
      }
      return txt;
    },
    _prefillTemplateValores() {
      const tpl = this.templateEscolhido;
      if (!tpl) {
        this.templateValoresEscolhidos = {};
        return;
      }
      // Preserva valores que já foram digitados pra outros parâmetros com
      // o mesmo nome — assim trocar de template não apaga tudo.
      const prev = this.templateValoresEscolhidos || {};
      const novos = {};
      for (const p of (tpl.parametros || [])) {
        if (prev[p.nome]) {
          novos[p.nome] = prev[p.nome];
        } else if (p.nome === 'nome') {
          novos[p.nome] = this.nomePrimeiroContato;
        } else {
          novos[p.nome] = '';
        }
      }
      this.templateValoresEscolhidos = novos;
    },
    abrirModalTemplate() {
      if (!this.templatesAprovados.length) {
        const msg = 'Nenhum template aprovado disponível.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
        return;
      }
      // Default: padrão_inicial → primeiro aprovado.
      const inicial = this.templatePadrao || this.templatesAprovados[0];
      this.templateEscolhidoName = inicial.name;
      this._prefillTemplateValores();
      this.templateModal = { conteudoOriginal: '' };
    },
    async confirmarEnvioTemplate() {
      if (!this.templateModal || !this.conversaAtiva) return;
      const tpl = this.templateEscolhido;
      if (!tpl) return;
      if (!this.templateValoresValidos) {
        const msg = 'Preencha todos os parâmetros obrigatórios.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
        return;
      }
      this.enviandoTemplate = true;
      try {
        await this.iniciarConversa({
          contato_id: this.conversaAtiva.contato_id,
          canal_whatsapp_id: this.conversaAtiva.canal_whatsapp_id || null,
          modo: 'template',
          templateName: tpl.name,
          templateValores: this.templateValoresEscolhidos,
        });
        this.templateModal = null;
        // Janela continua "fechada" até o filiado responder — não recarrega
        // nem libera o composer. Só sai do modo "fechado" via webhook
        // de mensagem entrante.
      } catch (error) {
        const msg = (error && error.response && error.response.data && error.response.data.error) || 'Falha ao enviar template.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
      } finally {
        this.enviandoTemplate = false;
      }
    },
    cancelarEnvioTemplate() { this.templateModal = null; },

    abrirFinalizar() {
      this.mensagemFinalTexto = '';
      this.finalizarModalAberto = true;
    },
    cancelarFinalizar() {
      if (this.finalizandoConversa) return;
      this.finalizarModalAberto = false;
      this.mensagemFinalTexto = '';
    },
    async confirmarFinalizar() {
      if (!this.conversaAtiva || this.finalizandoConversa) return;
      const texto = String(this.mensagemFinalTexto || '').trim();
      // Se atendente quis enviar texto e a janela está fechada, bloqueia
      // antes mesmo de bater no backend (defesa local — backend também
      // valida e devolve 409 se passar).
      if (texto && this.bloqueadoPorJanela) {
        const msg = 'Janela 24h fechada — não é possível enviar texto livre. Deixe a mensagem vazia ou envie um template antes.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
        return;
      }
      this.finalizandoConversa = true;
      try {
        await this.finalizarConversa({
          conversaId: this.conversaAtiva.id,
          mensagemFinal: texto || null,
        });
        this.finalizarModalAberto = false;
        this.mensagemFinalTexto = '';
        // Recolhe o drawer pra sua forma inerte — a conversa finalizada
        // sai da tela; ainda fica acessível pelo kanban na coluna "Encerrado".
        this.selecionarConversa(null);
      } catch (error) {
        const msg = (error && error.response && error.response.data && error.response.data.error) || 'Falha ao finalizar conversa.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
      } finally {
        this.finalizandoConversa = false;
      }
    },

    _ehMobile() {
      return Boolean(window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches);
    },
    togglePainel() {
      if (this.painelAberto) this.fecharPainel();
      else this.abrirPainel();
    },
    abrirPainel() {
      this.painelAberto = true;
      this._salvarPainelAberto();
      // Mobile: painel em fullscreen + entrada no histórico, pra o "voltar"
      // (botão ou nativo do celular) fechar o painel e voltar pra conversa.
      if (this._ehMobile()) {
        document.body.classList.add('atendimento-painel');
        window.history.pushState({ atdPainel: true }, '');
        this._painelHistoryPushed = true;
      }
    },
    _fecharPainelEstado() {
      this.painelAberto = false;
      this._salvarPainelAberto();
      document.body.classList.remove('atendimento-painel');
    },
    fecharPainel() {
      // Se empurramos histórico (mobile), volta por ele — o popstate fecha o
      // painel, mantendo o back nativo consistente.
      if (this._painelHistoryPushed) {
        this._painelHistoryPushed = false;
        window.history.back();
      } else {
        this._fecharPainelEstado();
      }
    },
    _onPainelPop() {
      if (this.painelAberto) {
        this._painelHistoryPushed = false;
        this._fecharPainelEstado();
      }
    },
    _salvarPainelAberto() {
      if (this._ehMobile()) return; // no mobile o painel sempre começa fechado
      try { localStorage.setItem('chat-drawer-painel-aberto', this.painelAberto ? '1' : '0'); }
      catch (_e) { /* noop */ }
    },
    _lerPainelAberto() {
      try {
        if (window.matchMedia && window.matchMedia('(max-width: 767.98px)').matches) return false;
        return localStorage.getItem('chat-drawer-painel-aberto') === '1';
      } catch (_e) { return false; }
    },

    _onEsc(e) { if (e.key === 'Escape') this.fechar(); },
    _addEscListener() { window.addEventListener('keydown', this._onEsc); },
    _removeEscListener() { window.removeEventListener('keydown', this._onEsc); },

    async aoEnviar({ conteudo }) {
      try {
        await this.enviarMensagem({ conteudo });
      } catch (error) {
        const data = (error && error.response && error.response.data) || {};
        // Defesa em profundidade: se o backend devolveu 409 com sugestão
        // de template (janela 24h fechada), abrimos a modal mesmo que o
        // composer estivesse habilitado por engano.
        if (data.janela_fechada && data.sugestao_template) {
          // Sincroniza estado local da janela com o que o backend disse.
          this.janelaInfo = { aberta: false, ultima_entrada: data.ultima_entrada || null };
          // Garante que temos a lista de templates carregada antes de abrir
          // o modal (pra o select renderizar).
          if (!this.templates.length) await this._carregarTemplates();
          // Pré-seleciona o template sugerido pelo backend.
          const sug = data.sugestao_template;
          this.templateEscolhidoName = sug.name
            || (this.templatePadrao && this.templatePadrao.name)
            || (this.templatesAprovados[0] && this.templatesAprovados[0].name)
            || '';
          this._prefillTemplateValores();
          // Sobrescreve com valores sugeridos pelo backend (já vem com
          // primeiro nome do contato).
          if (sug.valores_sugeridos) {
            this.templateValoresEscolhidos = { ...this.templateValoresEscolhidos, ...sug.valores_sugeridos };
          }
          this.templateModal = { conteudoOriginal: conteudo };
          return;
        }
        const msg = data.error || 'Falha ao enviar mensagem.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
      }
    },
    async assumir() { if (this.conversaAtiva) await this.assumirConversa(this.conversaAtiva.id); },
    async liberar() { if (this.conversaAtiva) await this.liberarConversa(this.conversaAtiva.id); },
    async marcarOptOut() {
      if (!this.conversaAtiva) return;
      // eslint-disable-next-line no-alert
      const motivo = window.prompt('Motivo do opt-out (opcional):', '');
      if (motivo === null) return;
      try {
        await this.registrarOptOut({ conversaId: this.conversaAtiva.id, motivo: motivo || 'manual-atendente' });
      } catch (error) {
        const msg = (error && error.response && error.response.data && error.response.data.error) || 'Falha ao registrar opt-out.';
        this.$toast?.error?.(msg) || alert(msg); // eslint-disable-line no-alert
      }
    },
  },
};
</script>

<template>
  <transition :name="inline ? 'none' : 'slide-drawer'">
    <div
      v-if="visivel"
      class="chat-drawer"
      :class="{
        'chat-drawer--overlay shadow-lg': !inline,
        'chat-drawer--inline': inline,
        'painel-aberto': painelAberto,
      }"
    >
      <!-- Backdrop só no overlay/mobile -->
      <div v-if="!inline" class="chat-drawer-backdrop d-md-none" @click="fechar"></div>

      <!-- Empty state — só no modo inline quando não há conversa selecionada -->
      <div v-if="inline && !conversaAtiva" class="chat-drawer-empty">
        <div class="chat-drawer-empty-inner">
          <i class="ri-chat-3-line chat-drawer-empty-icon"></i>
          <h3>Atendimento</h3>
          <p>Selecione uma conversa para começar</p>
        </div>
      </div>

      <div v-else class="chat-drawer-content">
        <!-- Coluna principal: header + mensagens + composer -->
        <section class="chat-drawer-main">
          <header class="chat-drawer-header">
            <div class="d-flex align-items-center gap-2 flex-grow-1" style="min-width: 0;">
              <button
                v-if="!inline"
                type="button"
                class="btn btn-link p-0 chat-drawer-close"
                title="Fechar (Esc)"
                @click="fechar"
              >
                <i class="ri-close-line" style="font-size: 1.6rem;"></i>
              </button>
              <!-- Modo lista no mobile: voltar para a lista de conversas. -->
              <button
                v-if="inline"
                type="button"
                class="btn btn-link p-0 chat-drawer-close d-md-none"
                title="Voltar"
                @click="fechar"
              >
                <i class="ri-arrow-left-line" style="font-size: 1.5rem;"></i>
              </button>
              <div class="avatar-circulo">{{ iniciaisAvatar }}</div>
              <div class="flex-grow-1 cd-header-info">
                <div class="fw-bold cd-header-nome">{{ tituloBarra }}</div>
                <div class="cd-header-meta text-muted">
                  <template v-if="estagio">
                    <span class="cd-stage-selector">
                      <button ref="estagioBtn" type="button" class="cd-stage-btn" @click.stop="toggleEstagios">
                        <span class="dot" :style="{ background: estagio.cor || '#878a99' }"></span>
                        <span>{{ estagio.nome }}</span>
                        <i class="ri-arrow-down-s-line cd-stage-arrow"></i>
                      </button>
                      <Teleport to="body">
                        <div
                          v-if="estagioDropAberto"
                          class="cd-stage-dropdown"
                          :style="estagioDropPos"
                          @click.stop
                        >
                          <div class="cd-stage-dropdown-title">Mover para etapa</div>
                          <button
                            v-for="est in estagios"
                            :key="est.id"
                            type="button"
                            class="cd-stage-option"
                            :class="{ ativo: est.id === conversaAtiva.estagio_id }"
                            @click="selecionarEstagio(est.id)"
                          >
                            <span class="dot" :style="{ background: est.cor || '#878a99' }"></span>
                            <span class="flex-grow-1 text-truncate">{{ est.nome }}</span>
                            <i v-if="est.id === conversaAtiva.estagio_id" class="ri-check-line cd-stage-check"></i>
                          </button>
                        </div>
                      </Teleport>
                    </span>
                  </template>
                  <template v-if="atendente">
                    <span v-if="estagio" class="cd-meta-sep">·</span>
                    <span>{{ atendente.nome || atendente.name || `#${atendente.id}` }}</span>
                  </template>
                </div>
              </div>
            </div>
            <!-- Desktop/tablet: botões individuais -->
            <div class="d-none d-md-flex gap-1 flex-shrink-0">
              <button v-if="podeAssumir" type="button" class="btn btn-sm btn-outline-primary" title="Assumir esta conversa" @click="assumir">
                <i class="ri-user-add-line"></i><span class="d-none d-lg-inline ms-1">Assumir</span>
              </button>
              <button v-if="eMinha" type="button" class="btn btn-sm btn-outline-secondary" title="Liberar (volta pra fila)" @click="liberar">
                <i class="ri-user-unfollow-line"></i><span class="d-none d-lg-inline ms-1">Liberar</span>
              </button>
              <button v-if="statusConsentimento !== 'opt_out'" type="button" class="btn btn-sm btn-outline-danger" title="Registrar opt-out" @click="marcarOptOut">
                <i class="ri-forbid-line"></i><span class="d-none d-lg-inline ms-1">Opt-out</span>
              </button>
              <button type="button" class="btn btn-sm btn-outline-success" title="Finalizar conversa" @click="abrirFinalizar">
                <i class="ri-checkbox-circle-line"></i><span class="d-none d-lg-inline ms-1">Finalizar</span>
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" :title="painelAberto ? 'Ocultar painel' : 'Mostrar painel'" @click="togglePainel">
                <i :class="painelAberto ? 'ri-side-bar-line' : 'ri-side-bar-fill'"></i>
              </button>
            </div>

            <!-- Mobile: tudo dentro de um 3-pontos -->
            <div class="d-md-none flex-shrink-0 cd-acoes-menu">
              <button ref="acoesBtn" type="button" class="btn btn-link p-0 chat-drawer-close cd-acoes-btn" title="Mais ações" @click.stop="toggleMenuAcoes">
                <i class="ri-more-2-fill"></i>
              </button>
              <Teleport to="body">
                <div v-if="menuAcoesAberto" class="cd-acoes-dropdown" :style="menuAcoesPos" @click.stop>
                  <button v-if="podeAssumir" type="button" class="cd-acoes-item" @click="acaoDoMenu(assumir)">
                    <i class="ri-user-add-line"></i><span>Assumir</span>
                  </button>
                  <button v-if="eMinha" type="button" class="cd-acoes-item" @click="acaoDoMenu(liberar)">
                    <i class="ri-user-unfollow-line"></i><span>Liberar</span>
                  </button>
                  <button v-if="statusConsentimento !== 'opt_out'" type="button" class="cd-acoes-item cd-acoes-item--danger" @click="acaoDoMenu(marcarOptOut)">
                    <i class="ri-forbid-line"></i><span>Opt-out</span>
                  </button>
                  <button type="button" class="cd-acoes-item cd-acoes-item--success" @click="acaoDoMenu(abrirFinalizar)">
                    <i class="ri-checkbox-circle-line"></i><span>Finalizar</span>
                  </button>
                  <div class="cd-acoes-sep"></div>
                  <button type="button" class="cd-acoes-item" @click="acaoDoMenu(togglePainel)">
                    <i :class="painelAberto ? 'ri-side-bar-line' : 'ri-side-bar-fill'"></i><span>{{ painelAberto ? 'Ocultar painel' : 'Mostrar painel' }}</span>
                  </button>
                </div>
              </Teleport>
            </div>
          </header>

          <div v-if="bloqueadoPorOptOut" class="alert alert-warning m-2 py-2 small mb-0">
            <i class="ri-forbid-line me-1"></i>Filiado com opt-out registrado. Envio de mensagens bloqueado.
          </div>

          <MensagemList :mensagens="mensagensDaAtiva" />

          <!-- Banner: janela 24h fechada → bloqueia texto, oferece template. -->
          <div
            v-if="!bloqueadoPorOptOut && bloqueadoPorJanela"
            class="janela-fechada-banner"
          >
            <div class="janela-fechada-info">
              <i class="ri-time-line"></i>
              <div>
                <div class="fw-semibold">Janela de 24h fechada</div>
                <small class="text-danger">
                  <template v-if="expiraJanelaFmt">
                    Última mensagem do filiado em {{ expiraJanelaFmt }}.
                  </template>
                  <template v-else>
                    Contato ainda não respondeu nenhuma mensagem.
                  </template>
                  Envie um template aprovado para retomar contato.
                </small>
              </div>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-primary"
              :disabled="!templatePadrao"
              @click="abrirModalTemplate"
            >
              <i class="ri-send-plane-fill me-1"></i>Enviar<br>template
            </button>
          </div>

          <MensagemComposer
            :disabled="composerDisabled"
            :conversa-id="conversaAtiva && conversaAtiva.id"
            @enviar="aoEnviar"
          />
        </section>

        <!-- Modal: confirmação de envio de template padrão -->
        <div v-if="templateModal" class="tpl-modal-overlay" @click.self="cancelarEnvioTemplate">
          <div class="tpl-modal">
            <header class="tpl-modal-header">
              <i class="ri-time-line"></i>
              <h6 class="m-0">Enviar template — janela 24h fechada</h6>
            </header>
            <div class="tpl-modal-body">
              <p class="text-muted small mb-2">
                A Meta exige um template aprovado para retomar contato fora da janela
                de 24h. Escolha qual enviar.
              </p>

              <!-- Seletor de template -->
              <div class="mb-2">
                <label class="form-label small mb-1">Template</label>
                <select v-model="templateEscolhidoName" class="form-select form-select-sm">
                  <option v-for="t in templatesAprovados" :key="t.name" :value="t.name">
                    {{ t.name }}{{ t.padrao_inicial ? ' (padrão)' : '' }}{{ t.descricao ? ' — ' + t.descricao : '' }}
                  </option>
                </select>
              </div>

              <!-- Formulário de parâmetros (se o template tiver placeholders) -->
              <div
                v-if="templateEscolhido && (templateEscolhido.parametros || []).length"
                class="mb-2"
              >
                <div
                  v-for="p in templateEscolhido.parametros"
                  :key="p.nome"
                  class="mb-2"
                >
                  <label class="form-label small mb-1">
                    {{ p.label || p.nome }}
                    <span v-if="p.obrigatorio" class="text-danger">*</span>
                  </label>
                  <input
                    v-model="templateValoresEscolhidos[p.nome]"
                    type="text"
                    class="form-control form-control-sm"
                    :placeholder="`Valor para {{${p.indice}}}`"
                  />
                </div>
              </div>

              <!-- Prévia renderizada -->
              <div v-if="templateEscolhido" class="tpl-modal-card">
                <div class="tpl-modal-card-name">
                  <i class="ri-file-text-line"></i>
                  <strong>{{ templateEscolhido.name }}</strong>
                  <span class="badge bg-light text-dark ms-auto">{{ templateEscolhido.language }}</span>
                </div>
                <p class="tpl-modal-preview">{{ previewTemplateEscolhido }}</p>
                <div
                  v-if="templateEscolhido.botoes && templateEscolhido.botoes.length"
                  class="tpl-modal-buttons"
                >
                  <button
                    v-for="(btn, i) in templateEscolhido.botoes"
                    :key="i"
                    type="button"
                    class="tpl-modal-btn"
                    disabled
                  >
                    <i class="ri-reply-line"></i>{{ btn.texto }}
                  </button>
                </div>
              </div>

              <div v-if="templateModal.conteudoOriginal" class="tpl-modal-original">
                <small class="fw-semibold">Sua mensagem original (não será enviada):</small>
                <small class="d-block fst-italic">"{{ templateModal.conteudoOriginal }}"</small>
              </div>
            </div>
            <footer class="tpl-modal-footer">
              <button type="button" class="btn btn-link" :disabled="enviandoTemplate" @click="cancelarEnvioTemplate">
                Cancelar
              </button>
              <button type="button" class="btn btn-primary" :disabled="enviandoTemplate" @click="confirmarEnvioTemplate">
                <i v-if="enviandoTemplate" class="ri-loader-4-line spin me-1"></i>
                <i v-else class="ri-send-plane-fill me-1"></i>
                {{ enviandoTemplate ? 'Enviando...' : 'Enviar template' }}
              </button>
            </footer>
          </div>
        </div>

        <!-- Modal: finalizar conversa -->
        <div v-if="finalizarModalAberto" class="tpl-modal-overlay" @click.self="cancelarFinalizar">
          <div class="tpl-modal">
            <header class="tpl-modal-header">
              <i class="ri-checkbox-circle-line text-success"></i>
              <h6 class="m-0">Finalizar conversa</h6>
            </header>
            <div class="tpl-modal-body">
              <p class="text-muted small mb-2">
                A conversa será movida para "Encerrado". O filiado continua podendo
                ser contatado no futuro (não é opt-out).
              </p>
              <label class="form-label small mb-1">Mensagem final (opcional)</label>
              <textarea
                v-model="mensagemFinalTexto"
                class="form-control form-control-sm"
                rows="3"
                :placeholder="bloqueadoPorJanela
                  ? 'Janela 24h fechada — texto livre não permitido'
                  : 'Ex.: Obrigado pelo contato! Estamos à disposição.'"
                :disabled="bloqueadoPorJanela || finalizandoConversa"
                maxlength="1024"
              ></textarea>
              <div v-if="bloqueadoPorJanela" class="alert alert-warning py-1 px-2 mb-0 mt-2 d-flex align-items-center small">
                <i class="ri-information-line me-1"></i>
                <span>Janela 24h fechada — finalize sem mensagem ou envie um template antes.</span>
              </div>
              <small v-else class="text-muted d-block mt-1">
                Deixe em branco se quiser apenas mover a conversa pra "Encerrado" sem avisar o filiado.
              </small>
            </div>
            <footer class="tpl-modal-footer">
              <button type="button" class="btn btn-link" :disabled="finalizandoConversa" @click="cancelarFinalizar">
                Cancelar
              </button>
              <button type="button" class="btn btn-success" :disabled="finalizandoConversa" @click="confirmarFinalizar">
                <i v-if="finalizandoConversa" class="ri-loader-4-line spin me-1"></i>
                <i v-else class="ri-checkbox-circle-line me-1"></i>
                {{ finalizandoConversa ? 'Finalizando...' : 'Finalizar' }}
              </button>
            </footer>
          </div>
        </div>

        <!-- Painel lateral (Perfil / Etiquetas / Notas) -->
        <aside v-if="painelAberto" class="chat-drawer-aside">
          <header class="painel-header">
            <button type="button" class="btn btn-link p-0 painel-hbtn d-md-none" title="Voltar para a conversa" @click="fecharPainel">
              <i class="ri-arrow-left-line"></i>
            </button>
            <div class="painel-header-info">
              <div class="painel-header-titulo text-truncate">{{ tituloBarra }}</div>
              <div v-if="subtituloBarra && subtituloBarra !== tituloBarra" class="painel-header-sub text-truncate">
                {{ subtituloBarra }}
              </div>
            </div>
            <button type="button" class="btn btn-link p-0 painel-hbtn d-none d-md-inline-flex" title="Fechar painel" @click="fecharPainel">
              <i class="ri-close-line"></i>
            </button>
          </header>
          <nav class="painel-tabs">
            <button
              v-for="t in tabs"
              :key="t.key"
              type="button"
              class="painel-tab"
              :class="{ ativo: painelTab === t.key }"
              :title="t.label"
              @click="painelTab = t.key"
            >
              <i :class="t.icone"></i><span>{{ t.label }}</span>
            </button>
          </nav>
          <div class="painel-conteudo">
            <PainelPerfil v-if="painelTab === 'perfil' && contatoAtivo" :contato="contatoAtivo" />
            <PainelEtiquetas v-else-if="painelTab === 'etiquetas' && contatoAtivo" :contato="contatoAtivo" />
            <PainelNotas v-else-if="painelTab === 'notas' && contatoAtivo" :contato="contatoAtivo" />
          </div>
        </aside>
      </div>
    </div>
  </transition>
</template>

<style scoped>
.chat-drawer {
  display: flex;
  background: transparent;
}

/* --- Modo overlay (drawer fixo à direita, usado no Kanban) ------- */
.chat-drawer--overlay {
  position: fixed;
  inset: 0 0 0 auto;
  /* Ocupa metade da tela; dilata um pouco quando o painel lateral está aberto. */
  width: 50vw;
  max-width: 100vw;
  z-index: 1050;
  pointer-events: none;
}
.chat-drawer--overlay.painel-aberto { width: min(75vw, 1280px); }
.chat-drawer--overlay .chat-drawer-content {
  border-left: 1px solid var(--vz-border-color);
  pointer-events: auto;
}

/* --- Modo inline (renderizado dentro do split, usado na Lista) --- */
.chat-drawer--inline {
  position: relative;
  flex: 1;
  min-width: 0;
  height: 100%;
  width: auto;
}
.chat-drawer--inline .chat-drawer-content { border-left: none; }

.chat-drawer-backdrop { display: none; }
.chat-drawer-content {
  display: flex;
  flex: 1;
  background: var(--vz-body-bg);
  color: var(--vz-body-color);
  overflow: hidden;
}

/* --- Empty state (modo inline sem conversa selecionada) ---------- */
.chat-drawer-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--vz-secondary-bg);
  color: var(--vz-secondary-color);
}
.chat-drawer-empty-inner { text-align: center; padding: 2rem; }
.chat-drawer-empty-icon {
  font-size: 64px;
  opacity: 0.3;
  display: block;
  margin-bottom: 0.75rem;
}
.chat-drawer-empty h3 {
  font-size: 1.25rem;
  font-weight: 400;
  color: var(--vz-body-color);
  margin: 0 0 0.4rem;
}
.chat-drawer-empty p { font-size: 0.875rem; margin: 0; }

.chat-drawer-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}

.chat-drawer-aside {
  width: 360px;
  flex-shrink: 0;
  border-left: 1px solid var(--vz-border-color);
  background: var(--vz-secondary-bg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chat-drawer-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--vz-border-color);
  background: var(--vz-body-bg);
  min-height: 64px;
}
.chat-drawer-close { color: var(--vz-body-color); }
.chat-drawer-close:hover { color: var(--vz-primary); }

.cd-header-info { min-width: 0; }
/* Nome (ou número, quando não há nome): no máximo 2 linhas, com o dropdown
   de etapa logo abaixo. */
.cd-header-nome {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.2;
  word-break: break-word;
}

/* Padroniza os botões de ação do header pra terem a mesma altura/fonte
   dos inputs/buttons form-control-sm (Assumir, Liberar, Opt-out, painel). */
.chat-drawer-header .btn.btn-sm {
  height: calc(1.5em + 0.5rem + 2px); /* mesmo cálculo do .form-control-sm */
  padding: 0.25rem 0.5rem;
  font-size: 0.8125rem;
  line-height: 1.5;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  border-radius: 0.25rem;
}
.chat-drawer-header .btn.btn-sm i { font-size: 0.95rem; line-height: 1; }

.avatar-circulo {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--vz-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}

/* Mobile: header da conversa com as cores da topbar (vermelho + amarelo),
   igual Grupos/Notícias. */
@media (max-width: 767.98px) {
  .chat-drawer-header {
    background: var(--vz-primary, #971219);
    border-bottom-color: rgba(0, 0, 0, 0.18);
  }
  [data-bs-theme='dark'] .chat-drawer-header {
    background: var(--brand-primary-dark, #3f060a);
    border-bottom-color: rgba(0, 0, 0, 0.35);
  }
  .chat-drawer-header .fw-bold,
  .chat-drawer-header .text-muted,
  .chat-drawer-header .btn-link,
  .chat-drawer-header .cd-stage-btn,
  .chat-drawer-header i {
    color: var(--vz-yellow, #fff100) !important;
  }
  [data-bs-theme='dark'] .chat-drawer-header .fw-bold,
  [data-bs-theme='dark'] .chat-drawer-header .text-muted,
  [data-bs-theme='dark'] .chat-drawer-header .btn-link,
  [data-bs-theme='dark'] .chat-drawer-header .cd-stage-btn,
  [data-bs-theme='dark'] .chat-drawer-header i {
    color: var(--vz-yellow, #f2c94c) !important;
  }
  /* Avatar de iniciais em vermelho sumiria no header vermelho — inverte
     para amarelo com texto vermelho. */
  .chat-drawer-header .avatar-circulo {
    background: var(--vz-yellow, #fff100);
    color: var(--vz-primary, #971219);
  }
  [data-bs-theme='dark'] .chat-drawer-header .avatar-circulo {
    background: var(--vz-yellow, #f2c94c);
    color: var(--brand-primary-dark, #3f060a);
  }
  .chat-drawer-header .cd-stage-btn:hover { background: rgba(0, 0, 0, 0.12); }
}

.dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  vertical-align: middle;
}

/* --- Linha meta do header (telefone · estágio · atendente) ------- */
.cd-header-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 0.78rem;
  line-height: 1.3;
  color: var(--vz-secondary-color);
  min-width: 0;
}
.cd-meta-sep { opacity: 0.6; }

/* --- Menu de ações no mobile (3-pontos) ------------------------- */
.cd-acoes-menu { position: relative; }
.cd-acoes-btn {
  width: 40px; height: 40px;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.4rem; line-height: 1;
}
/* Dropdown renderizado em <body> via <Teleport> — position: fixed + :global
   (o nó sai do escopo do componente). Cores via tokens = light/dark ok. */
:global(.cd-acoes-dropdown) {
  position: fixed;
  min-width: 210px;
  background: var(--vz-body-bg, #fff);
  color: var(--vz-body-color, #212529);
  border: 1px solid var(--vz-border-color, #e9edef);
  border-radius: 8px;
  box-shadow: 0 8px 28px rgba(0,0,0,0.22), 0 4px 10px rgba(0,0,0,0.12);
  padding: 6px 0;
  z-index: 2000;
}
:global(.cd-acoes-item) {
  display: flex; align-items: center; gap: 10px;
  width: 100%; border: 0; background: transparent;
  color: var(--vz-body-color, #212529);
  font-size: 0.9rem; padding: 11px 16px;
  cursor: pointer; text-align: left;
  transition: background 0.1s;
}
:global(.cd-acoes-item i) { font-size: 1.1rem; width: 1.2rem; text-align: center; flex-shrink: 0; }
:global(.cd-acoes-item:hover) { background: var(--vz-tertiary-bg, rgba(0,0,0,0.05)); }
:global(.cd-acoes-item--danger) { color: var(--vz-danger, #dc3545); }
:global(.cd-acoes-item--success) { color: var(--vz-success, #198754); }
:global(.cd-acoes-sep) { height: 1px; background: var(--vz-border-color, #e9edef); margin: 6px 0; }

/* --- Seletor de estágio (mover conversa pelo header) ------------- */
.cd-stage-selector {
  position: relative;
  display: inline-flex;
  align-items: center;
}
.cd-stage-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  padding: 2px 6px;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.12s;
}
.cd-stage-btn:hover { background: rgba(0,0,0,0.07); }
.cd-stage-btn .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.cd-stage-arrow { font-size: 0.95rem; line-height: 1; opacity: 0.7; }
/* Renderizado em <body> via <Teleport> — usa position: fixed e regras
   :global porque o nó sai do escopo do componente. */
:global(.cd-stage-dropdown) {
  position: fixed;
  min-width: 220px;
  max-width: 280px;
  max-height: 320px;
  overflow-y: auto;
  background: var(--vz-body-bg, #fff);
  color: var(--vz-body-color, #212529);
  border: 1px solid var(--vz-border-color, #e9edef);
  border-radius: 6px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.22), 0 4px 10px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  z-index: 2000;
}
:global(.cd-stage-dropdown-title) {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--vz-secondary-color, #6c757d);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 4px 14px 6px;
}
:global(.cd-stage-option) {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  color: var(--vz-body-color, #212529);
  font-size: 0.85rem;
  padding: 8px 14px;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
}
:global(.cd-stage-option .dot) { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; display: inline-block; }
:global(.cd-stage-option:hover) { background: var(--vz-secondary-bg, #f3f3f3); }
:global(.cd-stage-option.ativo) { font-weight: 600; }
:global(.cd-stage-check) { color: var(--vz-primary, #971219); font-size: 1rem; flex-shrink: 0; }
[data-bs-theme="dark"] .cd-stage-btn:hover { background: rgba(255, 255, 255, 0.10); }

/* Header do painel (com voltar no mobile) */
.painel-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.75rem;
  min-height: 60px;
  border-bottom: 1px solid var(--vz-border-color);
  background: var(--vz-body-bg);
  flex-shrink: 0;
}
.painel-header-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.painel-header-titulo {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.2;
}
.painel-header-sub {
  font-size: 0.75rem;
  line-height: 1.2;
  color: var(--vz-secondary-color);
}
.painel-hbtn {
  width: 40px; height: 40px;
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 1.4rem; line-height: 1;
  color: var(--vz-body-color);
}

/* Abas modernas — segmented pills */
.painel-tabs {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--vz-border-color);
  background: var(--vz-body-bg);
}
.painel-tab {
  flex: 1;
  background: var(--vz-tertiary-bg, rgba(0, 0, 0, 0.05));
  border: 0;
  padding: 8px 8px;
  font-size: 0.8125rem;
  color: var(--vz-secondary-color);
  cursor: pointer;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  transition: background 0.15s, color 0.15s;
}
.painel-tab i { font-size: 1rem; line-height: 1; }
.painel-tab:hover { color: var(--vz-body-color); }
.painel-tab.ativo {
  background: var(--vz-primary);
  color: #fff;
  font-weight: 600;
}

/* Mobile: header do painel com as cores da topbar (vermelho/amarelo). */
@media (max-width: 767.98px) {
  .painel-header {
    background: var(--vz-primary, #971219);
    border-bottom-color: rgba(0, 0, 0, 0.18);
  }
  [data-bs-theme='dark'] .painel-header {
    background: var(--brand-primary-dark, #3f060a);
    border-bottom-color: rgba(0, 0, 0, 0.35);
  }
  .painel-header .painel-header-titulo,
  .painel-header .painel-header-sub,
  .painel-header .painel-hbtn,
  .painel-header i {
    color: var(--vz-yellow, #fff100) !important;
  }
  .painel-header .painel-header-sub { opacity: 0.8; }
  [data-bs-theme='dark'] .painel-header .painel-header-titulo,
  [data-bs-theme='dark'] .painel-header .painel-header-sub,
  [data-bs-theme='dark'] .painel-header .painel-hbtn,
  [data-bs-theme='dark'] .painel-header i {
    color: var(--vz-yellow, #f2c94c) !important;
  }
}

.painel-conteudo {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

/* --- Banner janela 24h fechada ----------------------------------- */
.janela-fechada-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: #f8d7da;
  border-top: 1px solid #f1aeb5;
  border-bottom: 1px solid #f1aeb5;
  padding: 0.6rem 1rem;
}
.janela-fechada-info {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  color: #842029;
  min-width: 0;
}
.janela-fechada-info i {
  font-size: 1.2rem;
  margin-top: 1px;
  flex-shrink: 0;
}
.janela-fechada-info small { color: #842029; opacity: 0.9; }
/* Botão "Enviar template": quebra em 2 linhas (Enviar / template) sem
   partir nenhuma palavra. */
.janela-fechada-banner .btn {
  flex-shrink: 0;
  white-space: normal;
  word-break: keep-all;
  overflow-wrap: normal;
  line-height: 1.2;
  text-align: center;
}

/* --- Modal de confirmação de template ---------------------------- */
.tpl-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}
.tpl-modal {
  background: var(--vz-body-bg);
  border-radius: 8px;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 12px 40px rgba(0,0,0,0.4);
  display: flex;
  flex-direction: column;
}
.tpl-modal-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--vz-border-color);
}
.tpl-modal-header i { font-size: 1.3rem; color: #f0a500; }
.tpl-modal-body { padding: 1rem; }
.tpl-modal-card {
  background: var(--vz-secondary-bg);
  border-radius: 6px;
  padding: 0.75rem;
  border-left: 3px solid var(--vz-primary);
}
.tpl-modal-card-name {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.4rem;
  font-size: 0.85rem;
}
.tpl-modal-preview {
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  white-space: pre-wrap;
  color: var(--vz-body-color);
}
.tpl-modal-buttons {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-top: 0.6rem;
  padding-top: 0.4rem;
  border-top: 1px solid rgba(0,0,0,0.06);
}
.tpl-modal-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: var(--vz-body-bg);
  border: 1px solid var(--vz-border-color);
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  color: #00a5f4;
  cursor: not-allowed;
}
.tpl-modal-original {
  margin-top: 0.6rem;
  padding: 0.5rem 0.6rem;
  background: #fff8e1;
  border-radius: 4px;
  color: #856404;
}
.tpl-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.4rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--vz-border-color);
}
@keyframes spin-rotate { to { transform: rotate(360deg); } }
.spin { display: inline-block; animation: spin-rotate 0.8s linear infinite; }

[data-bs-theme="dark"] .tpl-modal-original {
  background: #332701;
  color: #ffc107;
}

/* Mobile / md or smaller — overlay fullscreen */
@media (max-width: 991.98px) {
  .chat-drawer--overlay { width: 100vw !important; }
  .chat-drawer--overlay.painel-aberto .chat-drawer-aside {
    position: absolute;
    inset: 0 0 0 auto;
    width: 100%;
    z-index: 2;
  }
  .chat-drawer-aside { width: 100%; }
}

/* App mobile: o painel cobre a conversa inteira, sem a borda esquerda (que
   aparecia como um risco claro de 1px no canto). */
@media (max-width: 767.98px) {
  .chat-drawer-aside {
    position: absolute;
    inset: 0;
    width: 100%;
    border-left: 0;
    z-index: 3;
  }
}

/* Animation */
.slide-drawer-enter-active,
.slide-drawer-leave-active { transition: transform 0.25s ease; }
.slide-drawer-enter-from,
.slide-drawer-leave-to { transform: translateX(100%); }

[data-bs-theme="dark"] .chat-drawer-content {
  background: #03050a;
  border-left-color: #242a31;
}

/* Desktop only: no mobile o header usa as cores PCdoB (vermelho/amarelo),
   definidas no bloco @media (max-width: 767.98px) acima. */
@media (min-width: 768px) {
  [data-bs-theme="dark"] .chat-drawer-header {
    background: #060a0f;
    border-bottom-color: #242a31;
  }
}

[data-bs-theme="dark"] .chat-drawer-aside {
  background: #000000;
  border-color: #242a31;
}

[data-bs-theme="dark"] .painel-tabs {
  background: #000000;
  border-bottom-color: #242a31;
}

[data-bs-theme="dark"] .chat-drawer-header .btn-outline-primary {
  color: #7cc4ff;
  border-color: #2d6ea3;
  background: rgba(45, 110, 163, 0.2);
}
[data-bs-theme="dark"] .chat-drawer-header .btn-outline-primary:hover {
  color: #dff1ff;
  border-color: #3b86c4;
  background: rgba(59, 134, 196, 0.35);
}

[data-bs-theme="dark"] .chat-drawer-header .btn-outline-danger {
  color: #ff9aa5;
  border-color: #a63d4a;
  background: rgba(166, 61, 74, 0.2);
}
[data-bs-theme="dark"] .chat-drawer-header .btn-outline-danger:hover {
  color: #ffd9dd;
  border-color: #c7505e;
  background: rgba(199, 80, 94, 0.35);
}

[data-bs-theme="dark"] .chat-drawer-header .btn-outline-success {
  color: #85e7b0;
  border-color: #2f8c57;
  background: rgba(47, 140, 87, 0.2);
}
[data-bs-theme="dark"] .chat-drawer-header .btn-outline-success:hover {
  color: #dafbe8;
  border-color: #43a46d;
  background: rgba(67, 164, 109, 0.35);
}

</style>
